import React from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons (Leaflet expects images at /node_modules paths)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom SVG icon for live drivers
const makeDriverIcon = (color = "#DC2626", pulse = true) =>
  L.divIcon({
    className: "ema-driver-marker",
    html: `
      <div style="position:relative;width:36px;height:36px;">
        ${pulse ? `<div style="position:absolute;inset:0;border-radius:50%;background:${color};opacity:.3;animation:emaPulse 1.6s ease-out infinite;"></div>` : ""}
        <div style="position:absolute;inset:6px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 4px 10px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:12px;font-family:'Outfit',sans-serif;">●</div>
      </div>
      <style>@keyframes emaPulse{0%{transform:scale(.6);opacity:.6}100%{transform:scale(2);opacity:0}}</style>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -16],
  });

const pkgIcon = L.divIcon({
  className: "ema-pkg-marker",
  html: `<div style="width:28px;height:28px;background:#0A1128;border:3px solid white;border-radius:6px;box-shadow:0 4px 10px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:11px;font-family:'Outfit',sans-serif;">📦</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0][0], points[0][1]], 13);
    } else {
      map.fitBounds(points, { padding: [40, 40] });
    }
  }, [JSON.stringify(points)]); // eslint-disable-line
  return null;
}

/**
 * Generic live map.
 * Props:
 *  - drivers: [{ id, name, location: {lat,lng,accuracy_m,at}, active_bookings: [], is_online }]
 *  - packages: [{ id, lat, lng, label }]  (optional pickup/delivery markers)
 *  - height: css px
 *  - center: [lat,lng] default Copenhagen
 */
export default function LiveMap({ drivers = [], packages = [], height = 480, center = [55.6761, 12.5683], zoom = 10, focusDriverId = null }) {
  const driverPts = drivers.filter((d) => d.location).map((d) => [d.location.lat, d.location.lng]);
  const pkgPts = packages.map((p) => [p.lat, p.lng]);
  const all = [...driverPts, ...pkgPts];

  return (
    <div style={{ height }} className="border border-brand overflow-hidden" data-testid="live-map">
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {drivers.map((d) =>
          d.location ? (
            <React.Fragment key={d.id}>
              <Marker
                position={[d.location.lat, d.location.lng]}
                icon={makeDriverIcon(d.is_online ? "#DC2626" : "#94a3b8", d.is_online)}
              >
                <Popup>
                  <div style={{ fontFamily: "Outfit, sans-serif" }}>
                    <div style={{ fontWeight: 700, color: "#0A1128" }}>{d.name}</div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                      {d.is_online ? "● Online" : "○ Offline"}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                      {d.active_bookings?.length || 0} aktive leveringer
                    </div>
                    {d.location?.accuracy_m ? (
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                        Nøjagtighed: ±{Math.round(d.location.accuracy_m)} m
                      </div>
                    ) : null}
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                      {new Date(d.location.at).toLocaleTimeString("da-DK")}
                    </div>
                  </div>
                </Popup>
              </Marker>
              {d.location.accuracy_m ? (
                <Circle
                  center={[d.location.lat, d.location.lng]}
                  radius={Math.min(d.location.accuracy_m, 200)}
                  pathOptions={{ color: "#DC2626", fillColor: "#DC2626", fillOpacity: 0.06, weight: 1 }}
                />
              ) : null}
            </React.Fragment>
          ) : null
        )}
        {packages.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={pkgIcon}>
            <Popup>{p.label}</Popup>
          </Marker>
        ))}
        {!focusDriverId && all.length > 0 && <FitBounds points={all} />}
        {focusDriverId && (() => {
          const d = drivers.find((x) => x.id === focusDriverId);
          if (d?.location) return <FitBounds points={[[d.location.lat, d.location.lng]]} />;
          return null;
        })()}
      </MapContainer>
    </div>
  );
}
